'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '@/lib/app-api';
import type { UserPermissions, UserPermissionsResponse } from '@/types';
import {
  hasPermission as hasPermissionFromList,
  hasAnyPermission as hasAnyPermissionFromList,
  hasAllPermissions as hasAllPermissionsFromList,
} from '@/lib/permissions';

export interface PermissionContextType {
  permissions: UserPermissions | null;
  loading: boolean;
  error: string | null;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
  isAdmin: boolean;
  isManager: boolean;
  isCoordinator: boolean;
  isDeveloper: boolean;
  isClient: boolean;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  // Set mounted state after hydration to prevent localStorage access during SSR
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Load user permissions only when mounted and there's a valid token
  useEffect(() => {
    if (!isMounted) return;

    const token = localStorage.getItem('accessToken');
    
    // Safely check location after mount
    const isOnAuthPage = typeof window !== 'undefined' && 
      (window.location.pathname === '/login' ||
       window.location.pathname === '/forgot-password' ||
       window.location.pathname.startsWith('/reset-password'));

    if (token && !isOnAuthPage) {
      loadPermissions();
    } else {
      setPermissions(null);
      setLoading(false);
    }
  }, [isMounted]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user info from localStorage or API
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setPermissions(null);
        return;
      }

      // Fetch detailed permissions from API via shared client (handles base URL + auth header)
      const permissionData: UserPermissionsResponse = await authApi.getUserPermissions();
      const normalizedPermissions: UserPermissions =
        'effective_permissions' in permissionData
          ? permissionData
          : {
              user_id: '',
              role: '',
              effective_permissions: permissionData.permissions || [],
              module_permissions: {},
              dynamic_permissions: [],
              role_permissions: [],
            };
      setPermissions(normalizedPermissions);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshPermissions = async () => {
    await loadPermissions();
  };

  // Permission checking functions
  const hasPermission = (permission: string): boolean => {
    return hasPermissionFromList(
      { permissions: permissions?.effective_permissions || [] },
      permission
    );
  };

  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return hasAnyPermissionFromList(
      { permissions: permissions?.effective_permissions || [] },
      requiredPermissions
    );
  };

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return hasAllPermissionsFromList(
      { permissions: permissions?.effective_permissions || [] },
      requiredPermissions
    );
  };

  const hasRole = (role: string): boolean => {
    if (!permissions) return false;
    return permissions.role === role;
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!permissions) return false;
    return roles.includes(permissions.role);
  };

  // Convenience role checks. Use the assigned role first; read-only client
  // permissions must not make a client look like a developer.
  const normalizedRole = String(permissions?.role || '').toLowerCase();
  const isAdmin = hasAnyPermission(['admin:*', 'rbac:manage', 'settings:manage']);
  const isManager = hasAnyPermission(['users:manage', 'projects:manage', 'admin:*']);
  const isCoordinator = hasAnyPermission(['projects:update', 'tasks:update', 'admin:*']);
  const isClient = normalizedRole === 'client';
  const isDeveloper = ['developer', 'engineer'].includes(normalizedRole) ||
    (!isClient && hasAnyPermission(['tasks:update', 'bugs:update', 'tasks:manage', 'bugs:manage', 'admin:*']));

  const contextValue: PermissionContextType = {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    refreshPermissions,
    isAdmin,
    isManager,
    isCoordinator,
    isDeveloper,
    isClient
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// Custom hook to use permissions
export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Specific permission hooks
export const useHasPermission = (permission: string): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
};

export const useHasAnyPermission = (permissions: string[]): boolean => {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissions);
};

export const useHasAllPermissions = (permissions: string[]): boolean => {
  const { hasAllPermissions } = usePermissions();
  return hasAllPermissions(permissions);
};

export const useHasRole = (role: string): boolean => {
  const { hasRole } = usePermissions();
  return hasRole(role);
};

export const useHasAnyRole = (roles: string[]): boolean => {
  const { hasAnyRole } = usePermissions();
  return hasAnyRole(roles);
};

export const useRole = (): string | null => {
  const { permissions } = usePermissions();
  return permissions?.role || null;
};

export const useUserId = (): string | null => {
  const { permissions } = usePermissions();
  return permissions?.user_id || null;
};

// Module-specific permission hooks
export const useProjectPermissions = () => {
  const { permissions } = usePermissions();
  return {
    canRead: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'projects:read'),
    canCreate: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'projects:create'),
    canUpdate: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'projects:update'),
    canDelete: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'projects:delete'),
    canAssign: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'projects:assign')
  };
};

export const useTaskPermissions = () => {
  const { permissions } = usePermissions();
  return {
    canRead: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'tasks:read'),
    canCreate: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'tasks:create'),
    canUpdate: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'tasks:update'),
    canDelete: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'tasks:delete'),
    canAssign: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'tasks:assign')
  };
};

export const useUserPermissions = () => {
  const { permissions } = usePermissions();
  return {
    canRead: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'users:read'),
    canCreate: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'users:create'),
    canUpdate: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'users:update'),
    canDelete: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'users:delete'),
    canManage: hasPermissionFromList({ permissions: permissions?.effective_permissions || [] }, 'users:manage')
  };
};

export default PermissionContext;
